import Swal from 'sweetalert2';
import { buildPdfBlob } from './exportPdf';
import type { LoadedDocument } from './document';
import type { DocumentStrokes } from './strokes';

export type HomeworkContext = {
  fileId: string;
  studid: string;
  lessonid: string;
  sig: string;
};

export type SubmitFormData = {
  parentName: string;
  parentPhone: string;
  childName: string;
  childAge: string;
};

const baseSwal = {
  background: '#131932',
  color: '#e6e8ef',
  buttonsStyling: false,
  customClass: {
    popup: 'draw-swal-popup',
    title: 'draw-swal-title',
    htmlContainer: 'draw-swal-html',
    confirmButton: 'draw-swal-btn draw-swal-confirm',
    cancelButton: 'draw-swal-btn draw-swal-cancel',
  },
} as const;

export async function showSubmitForm(t: (k: string, f?: string) => string): Promise<SubmitFormData | null> {
  const escape = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]!));
  const html = `
    <div style="display:flex;flex-direction:column;gap:10px;text-align:start;">
      <label style="font-size:13px;color:#9aa3bf;">${escape(t('submit.parentName'))}
        <input id="swal-parent-name" type="text" class="swal2-input" style="width:100%;margin:6px 0 0;" autocomplete="name" />
      </label>
      <label style="font-size:13px;color:#9aa3bf;">${escape(t('submit.parentPhone'))}
        <input id="swal-parent-phone" type="tel" class="swal2-input" style="width:100%;margin:6px 0 0;" autocomplete="tel" inputmode="numeric" maxlength="10" placeholder="05XXXXXXXX" pattern="05[0-9]{8}" dir="ltr" />
      </label>
      <label style="font-size:13px;color:#9aa3bf;">${escape(t('submit.childName'))}
        <input id="swal-child-name" type="text" class="swal2-input" style="width:100%;margin:6px 0 0;" />
      </label>
      <label style="font-size:13px;color:#9aa3bf;">${escape(t('submit.childAge'))}
        <input id="swal-child-age" type="text" class="swal2-input" style="width:100%;margin:6px 0 0;" inputmode="numeric" maxlength="3" pattern="[0-9]+" dir="ltr" />
      </label>
    </div>
  `;
  const result = await Swal.fire({
    ...baseSwal,
    title: t('submit.title'),
    html,
    width: 480,
    showCancelButton: true,
    confirmButtonText: t('submit.send'),
    cancelButtonText: t('submit.cancel'),
    reverseButtons: true,
    focusConfirm: false,
    didOpen: () => {
      // фильтруем цифры на лету для phone и age
      const filterDigits = (id: string) => {
        const el = document.getElementById(id) as HTMLInputElement | null;
        if (!el) return;
        el.addEventListener('input', () => {
          const clean = el.value.replace(/\D/g, '');
          if (clean !== el.value) el.value = clean;
        });
      };
      filterDigits('swal-parent-phone');
      filterDigits('swal-child-age');
    },
    preConfirm: (): SubmitFormData | false => {
      const data: SubmitFormData = {
        parentName:  (document.getElementById('swal-parent-name')  as HTMLInputElement | null)?.value?.trim() ?? '',
        parentPhone: (document.getElementById('swal-parent-phone') as HTMLInputElement | null)?.value?.trim() ?? '',
        childName:   (document.getElementById('swal-child-name')   as HTMLInputElement | null)?.value?.trim() ?? '',
        childAge:    (document.getElementById('swal-child-age')    as HTMLInputElement | null)?.value?.trim() ?? '',
      };
      if (!data.parentName || !data.parentPhone || !data.childName || !data.childAge) {
        Swal.showValidationMessage(t('submit.missingFields'));
        return false;
      }
      if (!/^05\d{8}$/.test(data.parentPhone)) {
        Swal.showValidationMessage(t('submit.invalidPhone'));
        return false;
      }
      if (!/^\d{1,3}$/.test(data.childAge)) {
        Swal.showValidationMessage(t('submit.invalidAge'));
        return false;
      }
      return data;
    },
  });
  if (!result.isConfirmed) return null;
  return result.value as SubmitFormData;
}

export async function sendHomework(
  doc: LoadedDocument,
  strokes: DocumentStrokes,
  hw: HomeworkContext,
  t: (k: string, f?: string) => string
): Promise<boolean> {
  // Подтверждение
  const confirmed = await Swal.fire({
    ...baseSwal,
    title: t('hw.confirm.title'),
    text: t('hw.confirm.text'),
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: t('hw.confirm.yes'),
    cancelButtonText: t('submit.cancel'),
    reverseButtons: true,
    focusCancel: true,
  });
  if (!confirmed.isConfirmed) return false;

  Swal.fire({
    ...baseSwal,
    title: t('submit.sending'),
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => Swal.showLoading(),
  });

  try {
    const blob = await buildPdfBlob(doc, strokes);
    const fd = new FormData();
    const fileBase = doc.fileName.replace(/\.[^.]+$/, '');
    fd.append('file', blob, `${fileBase}-${hw.studid}.pdf`);
    fd.append('source_name', doc.fileName);
    // CRM подписывает URL по fileId (hash или basename — то что было в ?file=).
    // Backend проверяет sig по этому же fileId — потому его и шлём, а не basename.
    fd.append('file_id', hw.fileId);
    fd.append('studid', hw.studid);
    if (hw.lessonid) fd.append('lessonid', hw.lessonid);
    if (hw.sig) fd.append('sig', hw.sig);

    const res = await fetch('/admin/api.php?action=send', {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.ok) {
      const errCode = (data as { error?: string })?.error;
      const localizedErr =
        errCode === 'signature_required' ? t('hw.signatureRequired') :
        errCode === 'bad_signature' ? t('hw.signatureInvalid') :
        errCode || `HTTP ${res.status}`;
      Swal.fire({
        ...baseSwal,
        icon: 'error',
        title: t('submit.fail'),
        text: localizedErr,
      });
      return false;
    }
    Swal.fire({
      ...baseSwal,
      icon: 'success',
      title: t('submit.success'),
      timer: 2200,
      showConfirmButton: false,
    });
    return true;
  } catch (e) {
    Swal.fire({
      ...baseSwal,
      icon: 'error',
      title: t('submit.fail'),
      text: (e as Error).message || 'network',
    });
    return false;
  }
}

export async function sendDrawing(
  doc: LoadedDocument,
  strokes: DocumentStrokes,
  form: SubmitFormData,
  t: (k: string, f?: string) => string
): Promise<boolean> {
  // показываем индикатор отправки
  Swal.fire({
    ...baseSwal,
    title: t('submit.sending'),
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => Swal.showLoading(),
  });

  try {
    const blob = await buildPdfBlob(doc, strokes);
    const fd = new FormData();
    const fileBase = doc.fileName.replace(/\.[^.]+$/, '');
    fd.append('file', blob, `${fileBase}-marked.pdf`);
    fd.append('source_name', doc.fileName);
    fd.append('parent_name', form.parentName);
    fd.append('parent_phone', form.parentPhone);
    fd.append('child_name', form.childName);
    fd.append('child_age', form.childAge);

    const res = await fetch('/admin/api.php?action=send', {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.ok) {
      const errCode = (data as { error?: string })?.error || `HTTP ${res.status}`;
      Swal.fire({
        ...baseSwal,
        icon: 'error',
        title: t('submit.fail'),
        text: errCode,
      });
      return false;
    }
    Swal.fire({
      ...baseSwal,
      icon: 'success',
      title: t('submit.success'),
      timer: 2200,
      showConfirmButton: false,
    });
    return true;
  } catch (e) {
    Swal.fire({
      ...baseSwal,
      icon: 'error',
      title: t('submit.fail'),
      text: (e as Error).message || 'network',
    });
    return false;
  }
}
